/**
 * Referral gate for silent vendor discounts (e.g. Simply Health). A CTA opts in with
 * `data-referral-coupon` and optionally `data-referral-param` (default "userId").
 */

/** A CTA click that passed the referral gate. */
export interface Referral {
  /** Coupon Connect applies silently. */
  coupon: string
  /** The partner's own id for this member, carried through to the order. */
  userId: string
}

// Only the format is checked: the partner can't send any proof, so a made-up UUID
// unlocks the coupon. The API stores only lowercase UUIDs (`wp_is_uuid`).
const MEMBER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The coupon and member id for a CTA when the URL carries a valid member id, else `null`. */
export function resolveReferral(cta: Element, search: string): Referral | null {
  const coupon = cta.getAttribute('data-referral-coupon')?.trim()
  if (!coupon) return null
  const param = cta.getAttribute('data-referral-param')?.trim() || 'userId'
  const userId = new URLSearchParams(search).get(param)?.trim() ?? ''
  return MEMBER_ID.test(userId) ? { coupon, userId: userId.toLowerCase() } : null
}

function splitLink(link: string): { path: string; params: URLSearchParams; hash: string } {
  const hashIndex = link.indexOf('#')
  const hash = hashIndex >= 0 ? link.slice(hashIndex) : ''
  const base = hashIndex >= 0 ? link.slice(0, hashIndex) : link
  const queryIndex = base.indexOf('?')
  const path = queryIndex >= 0 ? base.slice(0, queryIndex) : base
  const query = queryIndex >= 0 ? base.slice(queryIndex + 1) : ''
  return { path, params: new URLSearchParams(query), hash }
}

/** Append the referral params to a Connect link, preserving any existing query/hash. */
export function withReferral(link: string, referral: Referral): string {
  const { path, params, hash } = splitLink(link)
  params.set('hiddenCoupon', referral.coupon)
  params.set('referralUserId', referral.userId)
  return `${path}?${params.toString()}${hash}`
}

/**
 * Append the current page query to an outbound link, letting current values
 * win over the link's own. Used on `a[data-evaro-preserve-query]` so a member
 * id survives a hop between landing pages (e.g. health hub → category page).
 */
export function withCurrentSearch(link: string, search: string): string {
  const sourceParams = new URLSearchParams(search)
  if (Array.from(sourceParams).length === 0) return link

  const { path, params, hash } = splitLink(link)
  for (const key of sourceParams.keys()) params.delete(key)
  for (const [key, value] of sourceParams) params.append(key, value)
  return `${path}?${params.toString()}${hash}`
}
